import { useAuth } from "@/contexts/AuthContext";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface Doctor {
  id: string;
  full_name: string;
  color?: string;
}

interface UseDoctorsByClinicOptions {
  includeGlobalFallback?: boolean;
  clinicalWritersOnly?: boolean;
}

export const useDoctorsByClinic = (
  clinicId?: string,
  { includeGlobalFallback = true, clinicalWritersOnly = false }: UseDoctorsByClinicOptions = {}
) => {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["doctors-by-clinic", clinicId, includeGlobalFallback, clinicalWritersOnly, user?.id],
    queryFn: async () => {
      if (!clinicId) return [];

      if (clinicalWritersOnly) {
        // Bound the profile read to this clinic's authorized doctors. Starting
        // from all active profiles makes the roster's RLS checks expensive.
        const { data: assignments, error: assignmentsError } = await supabase
          .from("user_clinic_roles")
          .select("user_id")
          .eq("clinic_id", clinicId)
          .eq("standard_role", "doctor")
          .eq("access_mode", "full");
        if (assignmentsError) throw assignmentsError;

        const doctorIds = [...new Set((assignments ?? []).map(({ user_id }) => user_id))];
        if (doctorIds.length === 0) return [];

        const { data: eligible, error: eligibleError } = await supabase.from("profiles")
          .select("id, full_name, color")
          .in("id", doctorIds)
          .eq("is_active", true)
          .order("full_name");
        if (eligibleError) throw eligibleError;
        return (eligible ?? []).map(({ id, full_name, color }) => ({ id, full_name, color })) as Doctor[];
      }

      // Get doctors who have active schedules for this clinic
      const { data, error } = await supabase
        .from("doctor_clinic_schedules")
        .select(`
          doctor_id,
          profiles!inner(id, full_name, color)
        `)
        .eq("clinic_id", clinicId)
        .eq("is_active", true);

      if (error) throw error;

      // Deduplicate doctors (in case a doctor has multiple schedules)
      const uniqueDoctors = data.reduce((acc, item) => {
        const doctor = item.profiles as unknown as Doctor;
        if (!acc.find(d => d.id === doctor.id)) {
          acc.push(doctor);
        }
        return acc;
      }, [] as Doctor[]);

      // If clinic-specific schedules found, return them
      if (uniqueDoctors.length > 0) {
        return uniqueDoctors.sort((a, b) => a.full_name.localeCompare(b.full_name));
      }

      // Fallback: Check if ANY schedules exist in the system
      const { data: anySchedules } = await supabase
        .from("doctor_clinic_schedules")
        .select("id")
        .eq("is_active", true)
        .limit(1);

      // If schedules exist system-wide but none for this clinic, return empty (proper filtering)
      if (anySchedules && anySchedules.length > 0) {
        return [];
      }

      // If NO schedules exist at all (system not configured / onboarding mode),
      // fall back to all doctors assigned to this clinic OR with doctor profile role
      const { data: clinicDoctors, error: clinicDoctorsError } = await supabase
        .from("profiles")
        .select(`
          id, 
          full_name,
          color,
          role,
          user_clinic_roles!inner(role, clinic_id)
        `)
        .eq("is_active", true)
        .eq("user_clinic_roles.clinic_id", clinicId)
        .order("full_name");

      if (clinicDoctorsError) throw clinicDoctorsError;

      // Filter to only include profiles with doctor-like roles
      const doctorRoles = ["associate_dentist", "clinic_incharge", "admin"];
      const doctorProfileRoles = ["doctor", "admin"];

      const filteredDoctors = (clinicDoctors || []).filter(d => {
        const clinicRole = (d.user_clinic_roles as any)?.[0]?.role;
        // Include if clinic role is doctor-like OR profile role is doctor
        return doctorRoles.includes(clinicRole) || doctorProfileRoles.includes(d.role || "");
      });

      // If still empty, try a broader query: all profiles with role = 'doctor' in the system
      if (filteredDoctors.length === 0 && includeGlobalFallback) {
        const { data: allDoctors, error: allDoctorsError } = await supabase
          .from("profiles")
          .select("id, full_name, color")
          .eq("is_active", true)
          .eq("role", "doctor")
          .order("full_name");

        if (allDoctorsError) throw allDoctorsError;
        return (allDoctors || []).map(d => ({
          id: d.id,
          full_name: d.full_name,
          color: d.color
        })) as Doctor[];
      }

      return filteredDoctors.map(d => ({
        id: d.id,
        full_name: d.full_name,
        color: d.color
      })) as Doctor[];
    },
    enabled: !!clinicId,
  });
};
